import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type LastDataRow, type LastDataSections, useLastData } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Card, Input, Space } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

const emptySections: LastDataSections = {
  commercial: [],
  network: [],
  opening: [],
  current: [],
};

const ReportsLastDataPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [meterSearch, setMeterSearch] = useState("");

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;
  const meterFilter = meterSearch.trim();

  const lastDataQuery = useLastData(regionID, objectID, meterFilter || undefined);
  const sections = lastDataQuery.data ?? emptySections;
  const commercialData = sections.commercial ?? [];
  const networkData = sections.network ?? [];
  const openingData = sections.opening ?? [];
  const currentData = sections.current ?? [];

  const commercialPagination = useStandardPagination(commercialData.length);
  const networkPagination = useStandardPagination(networkData.length);
  const openingPagination = useStandardPagination(openingData.length);
  const currentPagination = useStandardPagination(currentData.length);

  const columns = useMemo<ColumnsType<LastDataRow>>(
    () => [
      { title: "№ объекта", dataIndex: "object_id", width: 120 },
      {
        title: "Объект",
        dataIndex: "object_name",
        ellipsis: true,
        render: (value: string | undefined, row) => value || `Объект №${row.object_id}`,
      },
      { title: "№ фидера", dataIndex: "feeder_id", width: 120 },
      {
        title: "Фидер",
        dataIndex: "feeder_name",
        ellipsis: true,
        render: (value: string | undefined, row) => value || row.dispatcher_name || "—",
      },
      {
        title: "Измерения",
        dataIndex: "measurements",
        ellipsis: true,
        render: (value?: string) => value || "—",
      },
      {
        title: "Счётчик №",
        dataIndex: "meter_number",
        width: 140,
        render: (value?: number) => value ?? "—",
      },
      {
        title: "Название",
        dataIndex: "channel_label",
        ellipsis: true,
        render: (value: string | undefined, row) => value || row.feeder_name || "—",
      },
      {
        title: "Дата",
        dataIndex: "timestamp",
        width: 140,
        render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY") : "—"),
      },
      {
        title: "Время",
        dataIndex: "timestamp",
        width: 120,
        render: (value?: string) => (value ? dayjs(value).format("HH:mm") : "—"),
      },
      {
        title: "Интервал",
        dataIndex: "interval_index",
        width: 120,
        render: (value?: number) => value ?? "—",
      },
    ],
    [],
  );

  const renderSection = (
    title: string,
    data: LastDataRow[],
    paginationConfig: TablePaginationConfig,
  ) => (
    <Card key={title} title={title} styles={{ body: { padding: 0 } }}>
      <EnterpriseTable<LastDataRow>
        rowKey={(row) =>
          `${row.object_id}-${row.feeder_id}-${row.meter_number ?? "meter"}-${row.timestamp ?? "ts"}-${title}`
        }
        columns={columns}
        dataSource={data}
        loading={lastDataQuery.isFetching}
        pagination={paginationConfig}
        scroll={{ x: 1000 }}
        size="small"
        toolbar={{ reload: true, onReload: () => lastDataQuery.refetch() }}
      />
    </Card>
  );

  const ready = regionID !== undefined && objectID !== undefined;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/last-data", {
        region: regionID,
        object: objectID,
        meter: meterFilter || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const meterLabel = meterFilter || "Все счётчики";

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `Последние данные — ${objectLabel}`,
      body: `Отчёт: Последние данные\nОбъект: ${objectLabel}\nФильтр по счётчикам: ${meterLabel}\n\nВложение содержит все секции отчёта.`,
      filename: `last-data_${regionID ?? "r"}_${objectID ?? "obj"}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
    });
  }, [ready, currentObject, objectID, regionID, meterLabel, openComposer]);

  return (
    <PageLayoutWithHelp
      title="Последние данные по объекту"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              allowClear
              style={{ minWidth: 300, flex: 1 }}
            />
            <Input
              placeholder="Поиск по счётчику"
              style={{ width: 260 }}
              value={meterSearch}
              onChange={(e) => setMeterSearch(e.target.value)}
              allowClear
            />
          </FilterRow>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            title="Выберите объект, чтобы загрузить отчёт по последним данным. Таблицы станут доступны после выбора."
            showIcon
          />
        )}
        {ready && (
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            {renderSection("Последние коммерческий интервал", commercialData, commercialPagination)}
            {renderSection("Последние параметры электросети", networkData, networkPagination)}
            {renderSection("Последние показания на начало дня", openingData, openingPagination)}
            {renderSection("Последние текущие показания", currentData, currentPagination)}
          </Space>
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsLastDataPage;
